// src/components/PazYSalvoRoleManager.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Edit, Trash2, Save, CheckCircle, AlertTriangle } from 'lucide-react';
import { Users, Plus, X, RefreshCw } from './icons';
import { useNotificationContext } from '../contexts/NotificationContext';
import UserAutoComplete from './UserAutoComplete';
import { pazYSalvoAPI } from '../services/api';

export default function PazYSalvoRoleManager({ showHeader = true }) {
    const [assignments, setAssignments] = useState([]);
    const [users, setUsers] = useState([]);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [editingEmpresas, setEditingEmpresas] = useState([]);
    const [newAssignment, setNewAssignment] = useState({
        departamento: '',
        rol: '',
        rolAdicional: '',
        userId: '',
        empresasAdicionales: []
    });
    const [empresas, setEmpresas] = useState([]);
    const [selectedEmpresas, setSelectedEmpresas] = useState([]);
    const [multipleAssignments, setMultipleAssignments] = useState([]);
    const [showMultipleModal, setShowMultipleModal] = useState(false);
    const [showMultipleRolesInModal, setShowMultipleRolesInModal] = useState(false);

    const { alertSuccess, alertError } = useNotificationContext();

    const roles = [
        'JefeInmediato',
        'RRHH',
        'Informatica',
        'Contabilidad',
        'GerenciaFinanzas',
        'GerenteGeneral'
    ];

    const departamentos = [
        'General',
        'IT',
        'Finanzas',
        'RRHH',
        'Gerencia',
        'Operaciones'
    ];

    useEffect(() => {
        loadData();
        loadEmpresas();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                loadAssignments(),
                loadUsers(),
                loadConfig()
            ]);
        } catch (error) {
            console.error('Error al cargar datos:', error);
            alertError('Error al cargar los datos');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateMultipleAssignments = async () => {
        if (multipleAssignments.length === 0) {
            alertError('Debe agregar al menos una asignación');
            return;
        }

        if (!newAssignment.userId) {
            alertError('Debe seleccionar un usuario');
            return;
        }

        try {
            setLoading(true);

            // Obtener el usuario seleccionado
            const usuario = users.find(u => u.id === newAssignment.userId);
            if (!usuario) {
                alertError('Usuario no encontrado');
                return;
            }

            const userIdParams = buildUserIdParams(usuario);
            let successCount = 0;
            let errorCount = 0;

            // Crear cada asignación
            for (const assignment of multipleAssignments) {
                if (!assignment.rol || !assignment.departamento) {
                    errorCount++;
                    continue;
                }

                try {
                    const assignmentData = { ...assignment, ...userIdParams };
                    await pazYSalvoAPI.createAssignment(assignmentData);
                    successCount++;
                } catch (error) {
                    console.error('Error creando asignación:', error);
                    errorCount++;
                }
            }

            if (successCount > 0) {
                alertSuccess(`${successCount} asignación(es) creada(s) exitosamente`);
                loadData();
                setShowMultipleModal(false);
                setMultipleAssignments([]);
                resetForm();
            }

            if (errorCount > 0) {
                alertError(`${errorCount} asignación(es) fallaron`);
            }

        } catch (error) {
            console.error('Error creando asignaciones múltiples:', error);
            alertError('Error al crear las asignaciones');
        } finally {
            setLoading(false);
        }
    };

    const DEFAULT_CONFIG = {
        nombre: 'Configuración por Defecto',
        activo: true,
        permiteDelegacion: true,
        firmas: [
            { id: 1, rol: 'JefeInmediato', orden: 1, obligatorio: true },
            { id: 2, rol: 'RRHH', orden: 2, obligatorio: true },
            { id: 3, rol: 'Informatica', orden: 3, obligatorio: true },
            { id: 4, rol: 'Contabilidad', orden: 4, obligatorio: true },
            { id: 5, rol: 'GerenciaFinanzas', orden: 5, obligatorio: true }
        ]
    };

    const loadEmpresas = async () => {
        try {
            const res = await pazYSalvoAPI.getEmpresasConfig();
            setEmpresas(res.data || []);
        } catch (error) {
            console.error('Error cargando empresas:', error);
        }
    };

    const loadAssignments = async () => {
        try {
            const res = await pazYSalvoAPI.getAssignments();
            setAssignments(res.data || []);
        } catch (error) {
            console.error('Error al cargar asignaciones:', error);
            setAssignments([]);
        }
    };

    const loadUsers = async () => {
        try {
            const res = await pazYSalvoAPI.getUsers();
            const data = res.data || [];
            // Eliminar duplicados por ID, manteniendo el primero
            const uniqueUsers = data.filter((user, index, arr) =>
                arr.findIndex(u => u.id === user.id) === index
            );
            setUsers(uniqueUsers);
        } catch (error) {
            console.error('Error al cargar usuarios:', error);
            setUsers([]);
        }
    };

    const loadConfig = async () => {
        try {
            const res = await pazYSalvoAPI.getConfig();
            setConfig(res.data);
        } catch (error) {
            console.error('Error al cargar configuración:', error);
            setConfig(DEFAULT_CONFIG);
        }
    };

    const handleUsuarioChange = (userId) => {
        setNewAssignment(prev => ({
            ...prev,
            userId: userId
        }));

        // Capturar automáticamente el departamento del usuario
        if (userId) {
            const usuario = users.find(u => u.id === userId);
            if (usuario && usuario.departamento) {
                setNewAssignment(prev => ({
                    ...prev,
                    departamento: usuario.departamento
                }));
            }
        }
    };

    // Si el usuario aun no tiene cuenta de acceso, el backend la crea automaticamente
    // (misma logica que ya usaba la resolucion automatica del Jefe Directo al crear un
    // Paz y Salvo). Se devuelven siempre ambas claves para pisar cualquier `userId`
    // previo que traiga el objeto que se está enviando (ej. newAssignment.userId, que
    // en realidad guarda el id de nómina, no el de la cuenta de acceso).
    const buildUserIdParams = (usuario) => (
        usuario.authUserId
            ? { userId: usuario.authUserId, nominaUsuarioId: undefined }
            : { userId: undefined, nominaUsuarioId: usuario.id }
    );

    const handleEmpresaToggle = (empresa) => {
        setSelectedEmpresas(prev => {
            if (prev.includes(empresa)) {
                return prev.filter(e => e !== empresa);
            } else {
                return [...prev, empresa];
            }
        });
    };

    const handleEditingEmpresaToggle = (empresa) => {
        setEditingEmpresas(prev => {
            if (prev.includes(empresa)) {
                return prev.filter(e => e !== empresa);
            } else {
                return [...prev, empresa];
            }
        });
    };

    const resetForm = () => {
        setNewAssignment({
            departamento: '',
            rol: '',
            rolAdicional: '',
            userId: '',
            empresasAdicionales: []
        });
        setSelectedEmpresas([]);
        setMultipleAssignments([]);
        setShowMultipleRolesInModal(false);
    };

    const addMultipleAssignment = () => {
        setMultipleAssignments(prev => [...prev, {
            departamento: '',
            rol: '',
            empresasAdicionales: []
        }]);
    };

    const removeMultipleAssignment = (index) => {
        setMultipleAssignments(prev => prev.filter((_, i) => i !== index));
    };

    const updateMultipleAssignment = (index, field, value) => {
        setMultipleAssignments(prev => prev.map((assignment, i) =>
            i === index ? { ...assignment, [field]: value } : assignment
        ));
    };

    const handleMultipleEmpresaToggle = (index, empresa) => {
        setMultipleAssignments(prev => prev.map((assignment, i) => {
            if (i === index) {
                const empresas = assignment.empresasAdicionales || [];
                if (empresas.includes(empresa)) {
                    return { ...assignment, empresasAdicionales: empresas.filter(e => e !== empresa) };
                } else {
                    return { ...assignment, empresasAdicionales: [...empresas, empresa] };
                }
            }
            return assignment;
        }));
    };

    const addRoleToModal = () => {
        setMultipleAssignments(prev => [...prev, {
            departamento: newAssignment.departamento,
            rol: '',
            empresasAdicionales: [...selectedEmpresas]
        }]);
    };

    const removeRoleFromModal = (index) => {
        setMultipleAssignments(prev => prev.filter((_, i) => i !== index));
    };

    const updateRoleInModal = (index, field, value) => {
        setMultipleAssignments(prev => prev.map((assignment, i) =>
            i === index ? { ...assignment, [field]: value } : assignment
        ));
    };

    const handleModalEmpresaToggle = (index, empresa) => {
        setMultipleAssignments(prev => prev.map((assignment, i) => {
            if (i === index) {
                const empresas = assignment.empresasAdicionales || [];
                if (empresas.includes(empresa)) {
                    return { ...assignment, empresasAdicionales: empresas.filter(e => e !== empresa) };
                } else {
                    return { ...assignment, empresasAdicionales: [...empresas, empresa] };
                }
            }
            return assignment;
        }));
    };

    const handleCreateAssignment = async () => {
        if (!newAssignment.userId) {
            alertError('Debe seleccionar un usuario');
            return;
        }

        // Si no hay rol principal, mostrar error
        if (!newAssignment.rol) {
            alertError('Debe seleccionar un rol');
            return;
        }

        // Si hay rol adicional, crear múltiples asignaciones
        if (newAssignment.rolAdicional && newAssignment.rolAdicional !== 'show') {
            await handleCreateTwoAssignments();
            return;
        }

        try {
            // Obtener el usuario seleccionado
            const usuario = users.find(u => u.id === newAssignment.userId);
            if (!usuario) {
                alertError('Usuario no encontrado');
                return;
            }

            // Crear la asignación -- si el usuario no tiene cuenta de acceso todavía,
            // el backend la crea automáticamente
            const assignmentData = {
                ...newAssignment,
                ...buildUserIdParams(usuario),
                empresasAdicionales: selectedEmpresas
            };

            const result = await pazYSalvoAPI.createAssignment(assignmentData);
            alertSuccess(`Asignación creada exitosamente para ${result.data?.empresasAsignadas ?? 1} empresa(s)`);
            setShowCreateModal(false);
            resetForm();
            loadAssignments();
        } catch (error) {
            console.error('Error al crear asignación:', error.message);
            alertError(error.response?.data?.message || error.message || 'Error al crear la asignación');
        }
    };

    const handleCreateTwoAssignments = async () => {
        try {
            setLoading(true);

            // Obtener el usuario seleccionado
            const usuario = users.find(u => u.id === newAssignment.userId);
            if (!usuario) {
                alertError('Usuario no encontrado');
                return;
            }

            const userIdParams = buildUserIdParams(usuario);
            let successCount = 0;
            let errorCount = 0;

            // Crear asignación principal
            try {
                await pazYSalvoAPI.createAssignment({
                    departamento: newAssignment.departamento,
                    rol: newAssignment.rol,
                    ...userIdParams,
                    empresasAdicionales: selectedEmpresas
                });
                successCount++;
            } catch (error) {
                console.error('Error creando asignación principal:', error);
                errorCount++;
            }

            // Crear asignación adicional
            try {
                await pazYSalvoAPI.createAssignment({
                    departamento: newAssignment.departamento,
                    rol: newAssignment.rolAdicional,
                    ...userIdParams,
                    empresasAdicionales: selectedEmpresas
                });
                successCount++;
            } catch (error) {
                console.error('Error creando asignación adicional:', error);
                errorCount++;
            }

            if (successCount > 0) {
                alertSuccess(`${successCount} asignación(es) creada(s) exitosamente`);
                loadData();
                setShowCreateModal(false);
                resetForm();
            }

            if (errorCount > 0) {
                alertError(`${errorCount} asignación(es) fallaron`);
            }

        } catch (error) {
            console.error('Error creando asignaciones:', error);
            alertError('Error al crear las asignaciones');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateMultipleAssignmentsFromModal = async () => {
        try {
            setLoading(true);

            // Obtener el usuario seleccionado
            const usuario = users.find(u => u.id === newAssignment.userId);
            if (!usuario) {
                alertError('Usuario no encontrado');
                return;
            }

            const userIdParams = buildUserIdParams(usuario);
            let successCount = 0;
            let errorCount = 0;

            // Crear asignación principal si tiene rol
            if (newAssignment.rol) {
                try {
                    await pazYSalvoAPI.createAssignment({
                        ...newAssignment,
                        ...userIdParams,
                        empresasAdicionales: selectedEmpresas
                    });
                    successCount++;
                } catch (error) {
                    console.error('Error creando asignación principal:', error);
                    errorCount++;
                }
            }

            // Crear asignaciones adicionales
            for (const assignment of multipleAssignments) {
                if (!assignment.rol || !assignment.departamento) {
                    errorCount++;
                    continue;
                }

                try {
                    await pazYSalvoAPI.createAssignment({ ...assignment, ...userIdParams });
                    successCount++;
                } catch (error) {
                    console.error('Error creando asignación adicional:', error);
                    errorCount++;
                }
            }

            if (successCount > 0) {
                alertSuccess(`${successCount} asignación(es) creada(s) exitosamente`);
                loadData();
                setShowCreateModal(false);
                resetForm();
            }

            if (errorCount > 0) {
                alertError(`${errorCount} asignación(es) fallaron`);
            }

        } catch (error) {
            console.error('Error creando asignaciones múltiples:', error);
            alertError('Error al crear las asignaciones');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateAssignment = async (id, updates) => {
        try {
            await pazYSalvoAPI.updateAssignment(id, updates);
            alertSuccess('Asignación actualizada exitosamente');
            setEditingAssignment(null);
            loadAssignments();
        } catch (error) {
            console.error('Error al actualizar asignación:', error);
            alertError(error.response?.data?.message || error.message);
        }
    };

    // Sin confirm/toast/recarga -- para usar en bucles (ej. borrar todos los
    // roles de un usuario) sin mostrar un dialogo de confirmacion por cada id.
    const desactivarAsignacionSilenciosa = async (id) => {
        try {
            await pazYSalvoAPI.deleteAssignment(id);
            return true;
        } catch (error) {
            console.error('Error al desactivar asignación:', error);
            return false;
        }
    };

    const handleDeleteAssignment = async (id) => {
        if (!window.confirm('¿Estás seguro de que quieres desactivar esta asignación?')) {
            return;
        }

        const ok = await desactivarAsignacionSilenciosa(id);
        if (ok) {
            alertSuccess('Asignación desactivada exitosamente');
            loadAssignments();
        } else {
            alertError('No se pudo desactivar la asignación');
        }
    };

    // Elimina un rol completo de un usuario (todas las empresas de ese
    // rol+departamento) desde la fila de "Asignaciones", sin tocar sus otros roles.
    const handleDeleteRoleGroup = async (userGroup, roleGroup) => {
        if (!window.confirm(`¿Eliminar el rol ${roleGroup.rol} (${roleGroup.departamento}) de ${userGroup.nombre}${userGroup.apellido ? ' ' + userGroup.apellido : ''}?`)) {
            return;
        }
        const resultados = await Promise.all(roleGroup.ids.map((id) => desactivarAsignacionSilenciosa(id)));
        if (resultados.every(Boolean)) {
            alertSuccess('Rol eliminado exitosamente');
        } else {
            alertError('Algunas asignaciones no se pudieron eliminar');
        }
        loadAssignments();
    };

    const handleEditRoleGroup = (roleGroup) => {
        const firstAssignment = assignments.find((a) => a.id === roleGroup.ids[0]);
        if (!firstAssignment) return;
        setEditingAssignment({ ...firstAssignment, id: roleGroup.ids[0] });
        setEditingEmpresas(roleGroup.empresas || []);
    };

    const getUserName = (userId) => {
        const user = users.find(u => u.id === userId);
        return user ? `${user.nombre} ${user.apellido}` : 'Usuario no encontrado';
    };

    const getUserRole = (userId) => {
        const user = users.find(u => u.id === userId);
        return user ? user.role : 'N/A';
    };

    // Función para agrupar asignaciones por usuario
    const groupAssignmentsByUser = (assignments) => {
        const grouped = {};

        assignments.forEach(assignment => {
            const key = `${assignment.userId}-${assignment.nombre}-${assignment.apellido}`;

            if (!grouped[key]) {
                grouped[key] = {
                    userId: assignment.userId,
                    nombre: assignment.nombre,
                    apellido: assignment.apellido,
                    userRole: assignment.userRole,
                    isActive: assignment.isActive,
                    assignments: []
                };
            }

            grouped[key].assignments.push({
                id: assignment.id,
                departamento: assignment.departamento,
                rol: assignment.rol,
                empresa: assignment.empresa
            });
        });

        // Agrupar asignaciones por rol y departamento, combinando empresas
        Object.values(grouped).forEach(userGroup => {
            const roleGroups = {};

            userGroup.assignments.forEach(assignment => {
                const roleKey = `${assignment.rol}-${assignment.departamento}`;

                if (!roleGroups[roleKey]) {
                    roleGroups[roleKey] = {
                        rol: assignment.rol,
                        departamento: assignment.departamento,
                        empresas: [],
                        ids: []
                    };
                }

                if (assignment.empresa && !roleGroups[roleKey].empresas.includes(assignment.empresa)) {
                    roleGroups[roleKey].empresas.push(assignment.empresa);
                }
                roleGroups[roleKey].ids.push(assignment.id);
            });

            // Convertir a array y ordenar por rol
            userGroup.assignments = Object.values(roleGroups).sort((a, b) => a.rol.localeCompare(b.rol));
        });

        return Object.values(grouped);
    };

    const groupedAssignments = groupAssignmentsByUser(assignments);

    if (loading) {
        return (
            <div className="animate-pulse space-y-4">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            {showHeader && (
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                            <Users className="h-6 w-6 mr-2 text-blue-600" />
                            Gestión de Roles de Paz y Salvo
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400">
                            Configura quién debe firmar cada tipo de documento
                        </p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={loadData}
                            className="flex items-center space-x-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            <RefreshCw className="h-4 w-4" />
                            <span>Actualizar</span>
                        </button>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Nueva Asignación</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Controles cuando no hay header */}
            {!showHeader && (
                <div className="flex justify-end items-center space-x-3">
                    <button
                        onClick={loadData}
                        className="flex items-center space-x-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                        <RefreshCw className="h-4 w-4" />
                        <span>Actualizar</span>
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        <span>Nueva Asignación</span>
                    </button>
                </div>
            )}

            {/* Configuración de firmas */}
            {config && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-400 mb-2">
                        Configuración de Firmas
                    </h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                        {config.nombre} - {config.permiteDelegacion ? 'Permite delegación' : 'No permite delegación'}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {config.firmas.map((firma, index) => (
                            <div key={`firma-${index}-${firma.rol}`} className="flex items-center space-x-2 text-sm">
                                <span className="font-medium text-blue-800 dark:text-blue-400">
                                    {firma.orden}.
                                </span>
                                <span className="text-blue-700 dark:text-blue-300">
                                    {firma.rol}
                                </span>
                                {firma.obligatorio && (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Lista de asignaciones */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Asignaciones de Roles ({groupedAssignments.length} usuarios, {assignments.length} asignaciones)
                    </h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Usuario Asignado
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Rol del Usuario
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Asignaciones
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {groupedAssignments.map((userGroup, index) => (
                                <tr key={`user-${userGroup.userId}-${index}`}>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        <div className="font-medium">
                                            {userGroup.apellido ? `${userGroup.nombre} ${userGroup.apellido}` : userGroup.nombre}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                        {userGroup.userRole}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        <div className="space-y-1.5">
                                            {userGroup.assignments.map((roleGroup, idx) => (
                                                <div key={`role-group-${roleGroup.rol}-${idx}`} className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center space-x-2 flex-wrap">
                                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                                            {roleGroup.rol}
                                                        </span>
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                            ({roleGroup.departamento})
                                                        </span>
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                            {roleGroup.empresas.length > 0 ? roleGroup.empresas.join(', ') : 'Sin empresa'}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center space-x-1 flex-shrink-0">
                                                        <button
                                                            onClick={() => handleEditRoleGroup(roleGroup)}
                                                            className="text-blue-600 hover:text-blue-700"
                                                            title={`Editar ${roleGroup.rol}`}
                                                        >
                                                            <Edit className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteRoleGroup(userGroup, roleGroup)}
                                                            className="text-red-600 hover:text-red-700"
                                                            title={`Eliminar ${roleGroup.rol}`}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${userGroup.isActive
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                            }`}>
                                            {userGroup.isActive ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={async () => {
                                                    if (!window.confirm(`¿Estás seguro de que quieres desactivar todas las asignaciones de ${userGroup.nombre}${userGroup.apellido ? ' ' + userGroup.apellido : ''}?`)) {
                                                        return;
                                                    }
                                                    const ids = userGroup.assignments.flatMap((roleGroup) => roleGroup.ids);
                                                    await Promise.all(ids.map((id) => desactivarAsignacionSilenciosa(id)));
                                                    alertSuccess('Asignaciones desactivadas exitosamente');
                                                    loadAssignments();
                                                }}
                                                className="text-red-600 hover:text-red-700"
                                                title="Eliminar todas las asignaciones"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de edición */}
            {editingAssignment && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setEditingAssignment(null);
                            setEditingEmpresas([]);
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Editar Asignación de Rol
                            </h3>
                            <button
                                onClick={() => {
                                    setEditingAssignment(null);
                                    setEditingEmpresas([]);
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Usuario (solo lectura) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Usuario
                                </label>
                                <input
                                    type="text"
                                    value={editingAssignment.apellido ? `${editingAssignment.nombre} ${editingAssignment.apellido}` : editingAssignment.nombre}
                                    readOnly
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                                />
                            </div>

                            {/* Departamento */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Departamento
                                </label>
                                <input
                                    type="text"
                                    value={editingAssignment.departamento}
                                    onChange={(e) => setEditingAssignment(prev => ({ ...prev, departamento: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Rol */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Rol
                                </label>
                                <select
                                    value={editingAssignment.rol}
                                    onChange={(e) => setEditingAssignment(prev => ({ ...prev, rol: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                >
                                    {roles.map(rol => (
                                        <option key={rol} value={rol}>{rol}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Empresas (igual que en creación) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Empresas
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    Selecciona las empresas donde este rol puede firmar Paz y Salvo
                                </p>
                                <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700">
                                    {empresas.length > 0 ? (
                                        empresas.map(empresa => (
                                            <label key={empresa} className="flex items-center space-x-2 py-1">
                                                <input
                                                    type="checkbox"
                                                    checked={editingEmpresas.includes(empresa)}
                                                    onChange={() => handleEditingEmpresaToggle(empresa)}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">{empresa}</span>
                                            </label>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                                            Cargando empresas...
                                        </p>
                                    )}
                                </div>
                                {editingEmpresas.length > 0 && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                                        {editingEmpresas.length} empresa(s) seleccionada(s)
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setEditingAssignment(null);
                                    setEditingEmpresas([]);
                                }}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => {
                                    // Actualizar la asignación con las empresas seleccionadas
                                    handleUpdateAssignment(editingAssignment.id, {
                                        departamento: editingAssignment.departamento,
                                        rol: editingAssignment.rol,
                                        empresasAdicionales: editingEmpresas
                                    });
                                    setEditingAssignment(null);
                                    setEditingEmpresas([]);
                                }}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            >
                                Guardar Cambios
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal de creación */}
            {showCreateModal && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowCreateModal(false);
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Nueva Asignación de Rol
                            </h3>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Usuario */}
                            <div>
                                <UserAutoComplete
                                    value={newAssignment.userId}
                                    usuarios={users}
                                    onChange={handleUsuarioChange}
                                    placeholder="Buscar usuario por nombre, departamento o email..."
                                    label="Usuario *"
                                    required={true}
                                />
                            </div>

                            {/* Departamento (capturado automáticamente) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Departamento
                                </label>
                                <input
                                    type="text"
                                    value={newAssignment.departamento}
                                    readOnly
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                                    placeholder="Se capturará automáticamente del usuario seleccionado"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Se captura automáticamente del usuario seleccionado
                                </p>
                            </div>

                            {/* Rol */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Rol *
                                </label>
                                <select
                                    value={newAssignment.rol}
                                    onChange={(e) => setNewAssignment(prev => ({ ...prev, rol: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Seleccionar rol...</option>
                                    {roles.map(rol => (
                                        <option key={rol} value={rol}>{rol}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Rol Adicional */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Rol Adicional (opcional)
                                </label>
                                {!newAssignment.rolAdicional || newAssignment.rolAdicional === '' ? (
                                    <button
                                        type="button"
                                        onClick={() => setNewAssignment(prev => ({ ...prev, rolAdicional: 'show' }))}
                                        className="w-full px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center space-x-2"
                                    >
                                        <Plus className="h-4 w-4 text-gray-500" />
                                        <span className="text-gray-500 dark:text-gray-400">Agregar rol adicional</span>
                                    </button>
                                ) : (
                                    <div className="flex space-x-2">
                                        <select
                                            value={newAssignment.rolAdicional === 'show' ? '' : newAssignment.rolAdicional}
                                            onChange={(e) => setNewAssignment(prev => ({ ...prev, rolAdicional: e.target.value }))}
                                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                        >
                                            <option value="">Seleccionar rol adicional...</option>
                                            {roles.map(rol => (
                                                <option key={rol} value={rol}>{rol}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => setNewAssignment(prev => ({ ...prev, rolAdicional: '' }))}
                                            className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Empresas Adicionales */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Empresas Adicionales
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    Selecciona empresas adicionales donde este rol también puede firmar Paz y Salvo
                                </p>
                                <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700">
                                    {empresas.length > 0 ? (
                                        empresas.map(empresa => (
                                            <label key={empresa} className="flex items-center space-x-2 py-1">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEmpresas.includes(empresa)}
                                                    onChange={() => handleEmpresaToggle(empresa)}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">{empresa}</span>
                                            </label>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                                            Cargando empresas...
                                        </p>
                                    )}
                                </div>
                                {selectedEmpresas.length > 0 && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                                        {selectedEmpresas.length} empresa(s) seleccionada(s)
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    resetForm();
                                }}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleCreateAssignment}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            >
                                {newAssignment.rolAdicional && newAssignment.rolAdicional !== 'show' ? 'Crear 2 Asignaciones' : 'Crear Asignación'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
}
